import { useMemo, useState } from "react";

import { Footer } from "@/components/layout/Footer";
import { Navbar } from "@/components/layout/Navbar";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useCourseItem } from "@/hooks/useCourses";
import { useCoursePermissions, hasPermission } from "@/hooks/usePermissions";
import { useAddFreeCourse } from "@/hooks/useCheckout";
import { useSEO } from "@/hooks/useSEO";
import { useAuth } from "@/lib/auth";
import { useAppStore } from "@/lib/store";
import { buildVimeoEmbedUrl, formatDuration } from "@/lib/helpers";
import { ArrowLeft, Bookmark, Play, ShoppingCart } from "lucide-react";
import { Link, useLocation, useParams } from "react-router-dom";
import { cn } from "@/lib/utils";

export function TutorialPage() {
  const location = useLocation();
  const { id } = useParams();
  const { user } = useAuth();
  const { data: course, loading, error } = useCourseItem(id ?? null);
  const { data: permissions, refetch: refetchPermissions } = useCoursePermissions();
  const { execute: addFreeCourse, loading: addingFree } = useAddFreeCourse();
  const { addToCart, cartItems, setCartOpen, toggleSaved, isSaved } = useAppStore();
  const [freeCourseMessage, setFreeCourseMessage] = useState<string | null>(null);
  const [freeCourseError, setFreeCourseError] = useState<string | null>(null);

  const checkoutStatus = useMemo(
    () => new URLSearchParams(location.search).get("checkout"),
    [location.search],
  );

  const isOwned = useMemo(() => {
    if (!user || !permissions || !id) return false;
    return hasPermission(permissions, id, user.id);
  }, [user, permissions, id]);

  const saved = id ? isSaved(id) : false;
  const inCart = id
    ? cartItems.some(
        (item) =>
          (item.type === "course" && item.course_id === id) ||
          (item.type === "bundle" && item.course_ids.includes(id)),
      )
    : false;

  useSEO({
    title: course?.name ?? "Tutorial",
    description: course?.short_description ?? undefined,
    canonical: id ? `/app/tutorial/${id}` : undefined,
    ogTitle: course?.name ?? undefined,
    ogDescription: course?.short_description ?? undefined,
    ogImage: course?.thumbnail_links_course ?? undefined,
  });

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-background text-foreground">
        <Navbar />
        <main className="mx-auto flex w-full max-w-4xl flex-col items-center gap-4 px-4 py-20 text-center sm:px-6">
          <div className="h-8 w-64 animate-pulse rounded bg-neutral-lightest" />
          <div className="h-4 w-96 animate-pulse rounded bg-neutral-lightest" />
        </main>
        <Footer />
      </div>
    );
  }

  // Not found
  if (!course || error) {
    return (
      <div className="min-h-screen bg-background text-foreground">
        <Navbar />
        <main className="mx-auto flex w-full max-w-4xl flex-col items-center gap-4 px-4 py-20 text-center sm:px-6">
          <h1 className="font-display text-2xl text-pink-darker sm:text-4xl">Tutorial not found</h1>
          <p className="text-neutral-dark">
            The requested tutorial is unavailable. Please browse the dance library.
          </p>
          <Button asChild className="rounded-round bg-pink-darker text-white hover:bg-pink-dark">
            <Link to="/dance-library">Go to Dance Library</Link>
          </Button>
        </main>
        <Footer />
      </div>
    );
  }

  const previewVideoUrl = course.preview_video_id?.video_url;
  const categories = course.category ?? [];
  const duration = course.total_duration ? formatDuration(course.total_duration) : null;

  const handleAddToCart = () => {
    addToCart({
      type: "course",
      course_id: course.id,
      name: course.name,
      price: course.price,
    });
    setCartOpen(true);
  };

  const handleAddFree = async () => {
    setFreeCourseMessage(null);
    setFreeCourseError(null);

    const result = await addFreeCourse(course.id);

    if (result.error) {
      setFreeCourseError(result.error);
      return;
    }

    await refetchPermissions();
    setFreeCourseMessage("Added to your dashboard.");
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navbar />

      <main className="mx-auto w-full max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        <Link
          to="/dance-library"
          className="mb-6 inline-flex items-center gap-1 text-sm text-neutral-dark hover:text-pink-darker"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Back to Dance Library
        </Link>

        <div className="grid gap-8 lg:grid-cols-[1.4fr_1fr]">
          {/* Left: Video / Preview */}
          <section className="space-y-4">
            <div className="relative aspect-video overflow-hidden rounded-large bg-neutral-900">
              {previewVideoUrl ? (
                <iframe
                  src={buildVimeoEmbedUrl(previewVideoUrl, { autoplay: true, muted: true })}
                  className="h-full w-full"
                  allow="autoplay; fullscreen; picture-in-picture"
                  allowFullScreen
                  title={`Preview: ${course.name}`}
                />
              ) : course.thumbnail_links_course ? (
                <>
                  <img
                    src={course.thumbnail_links_course}
                    alt={course.name}
                    className="h-full w-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black/30" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="inline-flex h-16 w-16 items-center justify-center rounded-round bg-white/90 text-pink-darker shadow-md">
                      <Play className="h-7 w-7" />
                    </div>
                  </div>
                </>
              ) : (
                <div className="flex h-full items-center justify-center">
                  <Play className="h-12 w-12 text-white/50" />
                </div>
              )}
            </div>

            {/* Course description */}
            {course.long_description && (
              <div className="rounded-main border border-neutral-lighter bg-white p-4">
                <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-neutral">
                  About this tutorial
                </h3>
                <div
                  className="text-sm leading-relaxed text-neutral-dark [&_a]:text-blue-brand [&_a]:underline [&_a]:underline-offset-2 hover:[&_a]:text-blue-brand/80"
                  dangerouslySetInnerHTML={{ __html: course.long_description }}
                />
              </div>
            )}
          </section>

          {/* Right: Details & Actions */}
          <aside className="space-y-4">
            <Card className="rounded-main border-neutral-lighter bg-white py-0 shadow-none">
              <CardContent className="space-y-4 px-5 py-5">
                <p className="text-xs font-semibold tracking-[0.12em] text-neutral uppercase">
                  Tutorial Detail
                </p>
                <h1 className="font-display text-2xl leading-tight text-pink-darker sm:text-4xl">
                  {course.name}
                </h1>
                <p className="text-sm text-neutral-dark">
                  Instructor: {course.Instructor?.name ?? "Khusbu Gemmati"}
                </p>

                {course.short_description && (
                  <p className="text-sm text-neutral-dark">{course.short_description}</p>
                )}

                {/* Tags */}
                <div className="flex flex-wrap gap-2 text-xs">
                  {categories.map((cat) => (
                    <span
                      key={cat.name}
                      className="rounded-round bg-neutral-lightest px-2.5 py-1 text-neutral-dark"
                    >
                      {cat.name}
                    </span>
                  ))}
                  {course.is_free && (
                    <span className="rounded-round bg-blue-brand/15 px-2.5 py-1 text-blue-brand">
                      Free
                    </span>
                  )}
                  {course.most_popular && (
                    <span className="rounded-round bg-yellow-brand/20 px-2.5 py-1 text-pink-darker">
                      Popular
                    </span>
                  )}
                </div>

                {/* Stats */}
                <div className="flex flex-wrap gap-4 text-sm text-neutral-dark">
                  {duration && <span>{duration} total</span>}
                  {course.total_videos != null && (
                    <span>
                      {course.total_videos} video{course.total_videos === 1 ? "" : "s"}
                    </span>
                  )}
                </div>

                {/* Price */}
                <p className="text-2xl font-semibold text-pink-dark">
                  {course.is_free ? "Free" : `$${course.price}`}
                </p>

                {/* Checkout status banners */}
                {checkoutStatus === "success" && (
                  <div className="rounded-main border border-blue-brand/20 bg-blue-brand/10 p-4 text-sm text-blue-brand">
                    Payment successful! The tutorial has been added to your dashboard.
                  </div>
                )}

                {checkoutStatus === "failed" && (
                  <div className="rounded-main border border-pink-dark/20 bg-pink-dark/5 p-4 text-sm text-pink-darker">
                    Checkout returned with a failed payment status. Please try again.
                  </div>
                )}

                {freeCourseMessage && (
                  <div className="rounded-main border border-blue-brand/20 bg-blue-brand/10 p-4 text-sm text-blue-brand">
                    {freeCourseMessage}
                  </div>
                )}

                {freeCourseError && (
                  <div className="rounded-main border border-pink-dark/20 bg-pink-dark/5 p-4 text-sm text-pink-darker">
                    {freeCourseError}
                  </div>
                )}

                {/* Action buttons */}
                {isOwned ? (
                  <div className="space-y-3">
                    <div className="rounded-main border border-blue-brand/20 bg-blue-brand/10 p-4 text-sm font-semibold text-blue-brand">
                      Tutorial unlocked
                    </div>
                    <Button
                      asChild
                      className="w-full rounded-round bg-pink-darker text-white hover:bg-pink-dark"
                    >
                      <Link to={`/app/dashboard/course?course_id=${course.id}`}>
                        Go to Course
                      </Link>
                    </Button>
                  </div>
                ) : course.is_free ? (
                  <div className="space-y-3">
                    {user ? (
                      <Button
                        type="button"
                        variant="outline"
                        className="w-full rounded-round border-[#762a5c] bg-white text-[#762a5c] hover:bg-[#faf1f6] hover:text-[#762a5c]"
                        disabled={addingFree}
                        onClick={handleAddFree}
                      >
                        {addingFree ? "Adding..." : "Add to Dashboard (Free)"}
                      </Button>
                    ) : (
                      <Button
                        asChild
                        className="w-full rounded-round bg-pink-darker text-white hover:bg-pink-dark"
                      >
                        <Link to="/app/auth/login">Log in to Add Free Course</Link>
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className="space-y-3">
                    <Button
                      type="button"
                      className={cn(
                        "w-full rounded-round bg-pink-darker text-white hover:bg-pink-dark",
                        inCart && "border border-[#762a5c] bg-white text-[#762a5c] hover:bg-[#faf1f6]",
                      )}
                      onClick={handleAddToCart}
                    >
                      <ShoppingCart className="mr-2 h-4 w-4" />
                      {inCart ? "Added to Cart" : `Add to Cart — $${course.price}`}
                    </Button>
                    {!user && (
                      <p className="text-center text-xs text-neutral">
                        <Link
                          to="/app/auth/login"
                          className="text-blue-brand hover:underline"
                        >
                          Log in
                        </Link>{" "}
                        to checkout
                      </p>
                    )}
                  </div>
                )}

                {/* Save button */}
                <button
                  type="button"
                  className="flex w-full items-center justify-center gap-2 rounded-round border border-neutral-light px-4 py-2.5 text-sm font-medium text-neutral-dark transition-colors hover:bg-neutral-lightest"
                  onClick={() => id && toggleSaved(id)}
                >
                  <Bookmark
                    className={cn("h-4 w-4", saved && "fill-pink-darker text-pink-darker")}
                  />
                  {saved ? "Saved" : "Save for Later"}
                </button>
              </CardContent>
            </Card>

            <Card className="rounded-main border-neutral-lighter bg-white py-0 shadow-none">
              <CardContent className="space-y-2 px-5 py-5 text-sm text-neutral-dark">
                <p className="font-semibold text-neutral-darker">Included with this tutorial</p>
                <p>Mirrored instruction breakdown</p>
                <p>Full performance cut with music</p>
                <p>Practice-ready sharing format</p>
              </CardContent>
            </Card>
          </aside>
        </div>
      </main>

      <Footer />
    </div>
  );
}
